import { orderReference } from "../../utils/orderReference";
import PageHeading from "../../components/PageHeading";
import SearchPending from "../../components/SearchPending";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { createListSearch } from "../../utils/listSearch";
import { apiErrorMessage } from "../../utils/apiError";
import { orderListBucket, orderStatusLabel } from "../../utils/orderStatus";
import { useEffect, useRef, useState } from "react";
import { Plus, Search, Eye, Loader2, ArrowLeft, RefreshCw } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import axios from "../../axios/AxiosInstance";
import CreateCommandeModal from "./CreateCommandeModal";
import { OrderDetails } from "./OrderDetails";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../components/reusableComponents/Dialog";

const date = value => value && !Number.isNaN(new Date(value).getTime()) ? new Date(value).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—";
export default function CommandesPage() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [appliedSearch, searching] = useDebouncedValue(search);
  const [status, setStatus] = useState("En cours");
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState(null);
  const [editor, setEditor] = useState(null);
  const [announcementPrompt, setAnnouncementPrompt] = useState(null);
  const [opening, setOpening] = useState(null);
  const request = useRef(null);
  const load = async () => {
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    setLoading(true); setError("");
    try { const {data} = await axios.get("order/freelance", { signal: controller.signal }); if (!controller.signal.aborted) setOrders(Array.isArray(data) ? data : []); }
    catch (error) { const detail = apiErrorMessage(error, "Impossible de charger les commandes. Cliquez sur Actualiser pour réessayer."); if (detail) setError(detail); }
    finally { if (!controller.signal.aborted) setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const consult = async order => {
    if (opening) return;
    setOpening(order._id);
    try {
      const {data} = await axios.get(`order/${order._id}`);
      const detail = data.order;
      if (!detail) throw new Error("Missing order");
      const prefix = String(order.postCode || "").slice(0,2);
      const excluded = ["contactedCandidates", "refusedByAgence", "acceptedCandidates", "confirmedCandidates", "submittedToClient", "contractedCandidates", "refusedCandidates"].flatMap(key => detail[key] || []).map(value => typeof value === "object" ? value._id : value);
      const candidates = (data.potentialCandidates || []).filter(candidate => !excluded.includes(candidate._id) && (!prefix || (candidate.locations || []).some(location => String(location).startsWith(prefix))));
      const parts = String(detail.contact || "").split(" ");
      const phone = parts.pop() || ""; const email = parts.pop() || "";
      setSelected({...detail,candidates,contactData:{name:parts.join(" "),email,phone}});
    } catch (error) { const detail = apiErrorMessage(error, "Impossible d’ouvrir cette commande. Réessayez."); if (detail) message.error(detail); }
    finally { setOpening(null); }
  };
  const matchesOrder = createListSearch(appliedSearch, ["qualification", "client.companyName", "companyName", "clientName", "town", "postCode", "address", "orderNumber", "orderReference", "typeContrat", "contractType", "contact.firstname", "contact.lastname", "contact.phone", "contact.email", "startDate", "dateRanges.startDate"]);
  const filtered = orders.filter(order => (status === "Toutes" || orderListBucket(order.status) === status) && matchesOrder({ ...order, orderReference: orderReference(order) }));
  const continueWithoutAnnouncement = () => {
    if (!announcementPrompt) return;
    setSelected(announcementPrompt.commande);
    setAnnouncementPrompt(null);
  };
  const prepareAnnouncement = () => {
    if (!announcementPrompt) return;
    const { commande, announcementDraft } = announcementPrompt;
    setAnnouncementPrompt(null);
    navigate("/annonces", {
      state: {
        announcementFromOrder: {
          sourceOrderId: commande._id || commande.orderNumber || "",
          draft: announcementDraft,
        },
      },
    });
  };
  if (selected) return <div>
    <button onClick={() => { setEditor(null); setSelected(null); load(); }} className="mb-5 inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600"><ArrowLeft size={17} />Retour aux commandes</button>
    <OrderDetails
      commande={selected}
      onClose={() => { setEditor(null); setSelected(null); load(); }}
      onDuplicate={order => setEditor({ mode: "duplicate", order })}
      onEdit={order => setEditor({ mode: "edit", order })}
    />
    {editor && <CreateCommandeModal
      close={() => setEditor(null)}
      orders={orders}
      setOrders={setOrders}
      initialOrder={editor.order}
      mode={editor.mode}
      onSaved={setSelected}
    />}
  </div>;
  return <div className="">
    <div className="mb-6 flex flex-wrap items-center justify-between gap-4"><div><PageHeading icon="orders" title={<> Commandes </>} description={<> Les besoins de vos clients, du recrutement à la clôture. </>} /></div><button onClick={() => setCreating(true)} className="inline-flex items-center gap-2 rounded-lg bg-blue-700 px-4 py-3 font-semibold text-white hover:bg-blue-800"><Plus size={18} />Nouvelle commande</button></div>
    <div className="mb-5 rounded-xl border border-slate-200 bg-white p-4">
      <div className="mb-4 flex flex-wrap gap-2">{["En cours","Terminée","Annulée","Toutes"].map(value => <button key={value} onClick={() => setStatus(value)} aria-pressed={status === value} className={`rounded-lg px-3 py-2 text-sm font-semibold ${status === value ? "bg-blue-50 text-blue-800" : "text-slate-500 hover:bg-slate-50"}`}>{value === "Terminée" ? "Terminées" : value === "Annulée" ? "Annulées" : value} <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600">{value === "Toutes" ? orders.length : orders.filter(order => orderListBucket(order.status) === value).length}</span></button>)}</div>
      <div className="flex flex-wrap items-center gap-3"><div className="relative min-w-0 flex-1"><Search size={17} className="absolute left-3 top-3 text-slate-400" /><input aria-label="Rechercher une commande" value={search} onChange={e => setSearch(e.target.value)} placeholder="Client, métier, ville, numéro…" className="w-full rounded-lg border border-slate-200 py-2.5 pl-10 pr-3 text-sm" /><div className="mt-2"><SearchPending pending={searching} /></div></div><button onClick={load} disabled={loading} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2.5 text-sm text-slate-600">{loading ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}Actualiser</button></div>
    </div>
    {error && <p role="alert" className="mb-4 rounded-lg bg-rose-50 p-4 text-sm text-rose-700">{error}</p>}
    {loading ? <p role="status" className="flex justify-center gap-2 py-10 text-slate-500"><Loader2 size={18} className="animate-spin" />Chargement des commandes…</p> : <div className="rounded-xl border border-slate-200 bg-white"><table className="w-full text-left text-sm"><thead><tr>{["N° commande", "Client", "Besoin", "Période / horaires", "Lieu", "État", "Action"].map(label => <th key={label} scope="col">{label}</th>)}</tr></thead><tbody>{filtered.map(order => <tr key={order._id}>
      <td><span className="whitespace-nowrap font-semibold tabular-nums text-blue-900">{orderReference(order)}</span></td>
      <td><strong className="text-slate-900">{order.client?.companyName || order.companyName || order.clientName || "—"}</strong></td>
      <td><p className="font-semibold text-slate-900">{order.qualification || "Métier à préciser"}</p><p className="mt-1 text-xs text-slate-500">{order.typeContrat || order.contractType || ""}{order.quantity != null && ` · ${order.quantity} personne(s)`}</p></td>
      <td>{(order.dateRanges?.length ? order.dateRanges : [{startDate:order.startDate,endDate:order.endDate}]).map((period,index) => <p key={index}>{date(period.startDate)}{period.endDate && period.endDate !== period.startDate && ` → ${date(period.endDate)}`}{period.startHour && <span className="ml-2 text-xs text-slate-500">{period.startHour}–{period.endHour}</span>}</p>)}<p className="mt-1 text-xs text-slate-500">{order.startHour || "—"} – {order.endHour || "—"}</p></td>
      <td>{[order.postCode,order.town].filter(Boolean).join(" ") || "—"}</td>
      <td><span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${orderListBucket(order.status) === "En cours" ? "bg-blue-50 text-blue-700" : orderListBucket(order.status) === "Terminée" ? "bg-green-50 text-green-700" : "bg-slate-100 text-slate-600"}`}>{orderStatusLabel(order.status)}</span></td>
      <td><button disabled={Boolean(opening)} onClick={() => consult(order)} className="inline-flex items-center gap-2 rounded-lg px-2 py-2 font-semibold text-blue-700 hover:bg-blue-50 disabled:opacity-50">{opening === order._id ? <Loader2 size={16} className="animate-spin" /> : <Eye size={16} />}Ouvrir</button></td>
    </tr>)}{!filtered.length && <tr><td colSpan={7} className="p-10 text-center text-slate-500">Aucune commande pour ces critères.{(search || status !== "Toutes") && <button onClick={() => { setSearch(""); setStatus("Toutes"); }} className="ml-2 font-semibold text-blue-700">Voir toutes les commandes</button>}</td></tr>}</tbody></table></div>}
    {creating && <CreateCommandeModal close={() => setCreating(false)} orders={orders} setOrders={setOrders} onCreated={setAnnouncementPrompt} />}
    <Dialog
      open={Boolean(announcementPrompt)}
      onOpenChange={(open) => {
        if (!open) continueWithoutAnnouncement();
      }}
    >
      <DialogContent className="max-w-lg bg-white">
        <div className="space-y-5 p-6">
          <DialogHeader>
            <DialogTitle>Créer une annonce pour cette commande ?</DialogTitle>
            <DialogDescription>
              La commande a bien été créée. Vous pouvez maintenant préparer une
              annonce préremplie, la vérifier puis l’enregistrer.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <button
              type="button"
              className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              onClick={continueWithoutAnnouncement}
            >
              Non, ouvrir la commande
            </button>
            <button
              type="button"
              className="rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800"
              onClick={prepareAnnouncement}
            >
              Oui, préparer l’annonce
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  </div>;
}
